// @flow
import _ from 'lodash'
import React, { Component } from 'react'
import { translations, translateFunc } from '../../shared/localize'
import FbAdInterests from './Interests/FbAdInterests'
import FieldSet from '../FieldSet'
import utils from '../FieldSet/utils'

import type { Node } from '../types'
import getJson from '../../services/getJson'
import LabeledWrapper from './LabeledWrapper'

type Props = {|
  fbAdInterests: Array<Object>,
  fbAdInterestsPath: string,
  providerInterests: Array<Node>,
  selectedIds: Array<number>,
  errors?: Array<string>,
  vkVisible: boolean,
  tmVisible: boolean,
  fbVisible: boolean,
  glVisible: boolean,
  onSelectedIdsChange: (Array<number>) => void,
  onFbAdInterestsChange: (Array<Object>) => void
|}

type State = {|
  isOpen: boolean,
  expandedIds: Object,
  selectedIds: Array<number>,
  searchString: string,
  interestList: Array<any>,
  searchedFbAdInterests?: Array<any>,
  interestTrees: Array<Object>,
  searchWasChangedWhileLoading: boolean,
  isFbInterestLoading: boolean,
  fbInterestLoadingError: ?string
|}

export default class Interests extends Component<Props, State> {
  t: Function
  updateInterestsFn: Function

  constructor(props: Props) {
    super(props)

    this.t = translateFunc(translations.interests)
    this.updateInterestsFn = _.debounce(() => this.updateInterests(), 1500)

    this.state = {
      isOpen: false,
      expandedIds: {},
      selectedIds: props.selectedIds,
      searchString: '',
      interestList: [],
      searchedFbAdInterests: [],
      interestTrees: [],
      searchWasChangedWhileLoading: false,
      isFbInterestLoading: false,
      fbInterestLoadingError: null
    }
  }

  componentDidUpdate(prevProps: Props) {
    if (
      _.xor(this.props.selectedIds, prevProps.selectedIds).length > 0 &&
      _.xor(this.state.selectedIds, this.props.selectedIds).length > 0
    ) {
      this.setState({ selectedIds: this.props.selectedIds }, this.updateInterestList)
    }
  }

  componentWillMount() {
    this.updateInterestList()
  }

  updateInterestList() {
    const { providerInterests } = this.props

    const interestList = _.map(providerInterests, (providerInterest) => {
      const { selectedIds } = this.state
      const { id, provider } = providerInterest

      return {
        ...providerInterest,
        provider: _.camelCase(provider),
        key: id,
        isSelected: _.includes(selectedIds, id)
      }
    })

    const interestTrees = utils.buildProviderTrees(interestList)

    this.setState({ interestList, interestTrees })

    this.props.onSelectedIdsChange(this.state.selectedIds)
  }

  render() {
    const { interestList } = this.state
    const interestListSelected = _.filter(interestList, (interest) => interest.isSelected)

    return (
      <LabeledWrapper label={this.t('label')} errors={this.props.errors}>
        <FieldSet.Layout>
          {this.renderSelected(interestListSelected)}
          <FieldSet.OpenButton label={this.t('open_modal')} onClick={() => this.openModal()} />
          {this.renderModal()}
        </FieldSet.Layout>
      </LabeledWrapper>
    )
  }

  renderSelected(selectedNodeList: Array<Object>) {
    const { vk, google, targetMail, facebook } = _.groupBy(selectedNodeList, 'provider')

    if (
      !this.props.vkVisible &&
      !this.props.tmVisible &&
      !this.props.fbVisible &&
      !this.props.glVisible
    )
      return null
    return (
      <FieldSet.Lines>
        {this.props.vkVisible && (
          <FieldSet.Line title={this.t('vk_provider')}>
            {this.renderSelectedByProvider(_.sortBy(vk, 'label'))}
          </FieldSet.Line>
        )}
        {this.props.glVisible && (
          <FieldSet.Line title={this.t('google_provider')}>
            {this.renderSelectedByProvider(_.sortBy(google, 'label'))}
          </FieldSet.Line>
        )}
        {this.props.tmVisible && (
          <FieldSet.Line title={this.t('target_mail_provider')}>
            {this.renderSelectedByProvider(_.sortBy(targetMail, 'label'))}
          </FieldSet.Line>
        )}
        {this.props.fbVisible && (
          <FieldSet.Line title={this.t('facebook_provider')}>
            {[
              ...this.renderSelectedByProvider(_.sortBy(facebook, 'label')),
              ...this.renderSelectedFbAdInterests()
            ]}
          </FieldSet.Line>
        )}
      </FieldSet.Lines>
    )
  }

  renderSelectedFbAdInterests(): Array<Function> {
    const { fbAdInterests, onFbAdInterestsChange } = this.props
    const collection = _.sortBy(fbAdInterests, 'label')

    return _.map(collection, (node) => (
      <FieldSet.SelectedInline
        key={node.id}
        node={node}
        onDeselect={(node) => onFbAdInterestsChange(_.reject(fbAdInterests, ['id', node.id]))}
      />
    ))
  }

  renderSelectedByProvider(selectedNodeList: Array<Object>): Array<Function> {
    return _.map(selectedNodeList, (node) => (
      <FieldSet.SelectedInline
        key={node.id}
        node={node}
        onDeselect={(node) => this.removeSelectedIds([node.id])}
      />
    ))
  }

  openModal() {
    this.setState({ isOpen: true })
  }

  closeModal() {
    this.setState({ isOpen: false })
  }

  renderModal() {
    const { searchString, isOpen } = this.state

    // prevent rendering modal when it's closed
    if (!isOpen) return null

    return (
      <FieldSet.Modal
        isOpen={isOpen}
        onClose={() => this.closeModal()}
        onSearchChange={(searchString) => this.searchChanged(searchString)}
        searchString={searchString}
        header={this.t('select_interests')}
        searchable
        searchPlaceholder={this.t('search')}
      >
        {_.isEmpty(searchString) ? this.renderTrees() : this.renderMatchedNodes()}
      </FieldSet.Modal>
    )
  }

  renderTrees(): any {
    const { interestTrees } = this.state
    const { vk, google, targetMail, facebook } = _.groupBy(interestTrees, 'provider')

    return (
      <FieldSet.Columns>
        {this.props.vkVisible && (
          <FieldSet.Column title={this.t('vk_provider')}>{this.renderTree(vk)}</FieldSet.Column>
        )}
        {this.props.glVisible && (
          <FieldSet.Column title={this.t('google_provider')}>
            {this.renderTree(google)}
          </FieldSet.Column>
        )}
        {this.props.tmVisible && (
          <FieldSet.Column title={this.t('target_mail_provider')}>
            {this.renderTree(targetMail)}
          </FieldSet.Column>
        )}
        {this.props.fbVisible && (
          <FieldSet.Column title={this.t('facebook_provider')}>
            {this.renderTree(facebook)}
            <FbAdInterests
              collection={this.props.fbAdInterests}
              onSelectChange={(node) => this.selectFbAdInterest(node)}
              searched
            />
          </FieldSet.Column>
        )}
      </FieldSet.Columns>
    )
  }

  renderTree(nodes: Array<Object>): Array<Function> {
    return _.map(_.sortBy(nodes, 'label'), (node) => (
      <FieldSet.Node
        node={node}
        key={node.id}
        expandedIds={this.state.expandedIds}
        triggerNodeExpanded={(id, expanded) => this.triggerNodeExpanded(id, expanded)}
        triggerNodeSelected={(node) => this.triggerNodeSelected(node)}
      />
    ))
  }

  renderMatchedNodes(): any {
    const { interestList } = this.state

    const mathchedNodes = this.matchedNodes(interestList)
    const mathchedNodeIds = _.map(mathchedNodes, 'id')
    const { vk, google, targetMail, facebook } = _.groupBy(mathchedNodes, 'provider')
    const { searchedFbAdInterests, fbInterestLoadingError } = this.state

    const fbAdInterestsWithSelectAttr = _.map(searchedFbAdInterests, (searchedInterest) => {
      const foundSelected = _.find(
        this.props.fbAdInterests,
        (fbAdInterest) => fbAdInterest.id === searchedInterest.id
      )
      return { ...searchedInterest, isSelected: !!foundSelected }
    })

    return (
      <div>
        <FieldSet.MatchedAction
          label={this.t('select_all')}
          onClick={() => this.addSelectedIds(mathchedNodeIds)}
        />
        <FieldSet.MatchedAction
          label={this.t('clear_all')}
          onClick={() => this.removeSelectedIds(mathchedNodeIds)}
        />

        <FieldSet.Columns>
          {this.props.vkVisible && (
            <FieldSet.Column title={this.t('vk_provider')}>
              {this.renderMatchedNodesByProvider(vk)}
            </FieldSet.Column>
          )}
          {this.props.glVisible && (
            <FieldSet.Column title={this.t('google_provider')}>
              {this.renderMatchedNodesByProvider(google)}
            </FieldSet.Column>
          )}
          {this.props.tmVisible && (
            <FieldSet.Column title={this.t('target_mail_provider')}>
              {this.renderMatchedNodesByProvider(targetMail)}
            </FieldSet.Column>
          )}
          {this.props.fbVisible && (
            <FieldSet.Column title={this.t('facebook_provider')}>
              {this.renderMatchedNodesByProvider(facebook)}
              <FbAdInterests
                collection={fbAdInterestsWithSelectAttr}
                onSelectChange={(node) => this.selectFbAdInterest(node)}
                searched={!_.isEmpty(searchedFbAdInterests)}
              />
              {fbInterestLoadingError && <FieldSet.Error>{fbInterestLoadingError}</FieldSet.Error>}
            </FieldSet.Column>
          )}
        </FieldSet.Columns>
      </div>
    )
  }

  renderMatchedNodesByProvider(nodes: Array<Node>): Array<Function> {
    nodes = nodes || []

    return _.map(nodes, (node: Node) => (
      <FieldSet.MatchedNode
        node={node}
        key={node.id}
        onSelectChange={(node) => this.triggerNodeSelected(node)}
      />
    ))
  }

  triggerNodeSelected(node: Node) {
    const { isSelected, id } = node

    this.setState((state) => {
      const { selectedIds, expandedIds, interestList } = state

      if (isSelected) {
        const parents = utils.findParents(node, interestList)
        const father = parents[0]

        if (father && _.isUndefined(expandedIds[father.id])) {
          const idsToExpand = _.reduce(
            parents,
            (merged, p) =>
              _.merge(merged, {
                [p.id]: true
              }),
            {}
          )
          return {
            selectedIds: _.without(selectedIds, id),
            expandedIds: { ...idsToExpand, ...expandedIds }
          }
        } else {
          return { selectedIds: _.without(selectedIds, id) }
        }
      } else {
        return { selectedIds: _.uniq([...selectedIds, id]) }
      }
    }, this.updateInterestList)
  }

  triggerNodeExpanded(id: number, expanded: boolean) {
    this.setState((state) => {
      return {
        expandedIds: { ...state.expandedIds, [id]: expanded }
      }
    }, this.updateInterestList)
  }

  addSelectedIds(ids: Array<number>) {
    this.setState((state) => {
      return { selectedIds: _.uniq([...state.selectedIds, ...ids]) }
    }, this.updateInterestList)
  }

  removeSelectedIds(ids: Array<number>) {
    this.setState((state) => {
      return { selectedIds: _.without(state.selectedIds, ...ids) }
    }, this.updateInterestList)
  }

  selectFbAdInterest(interest: Node) {
    const { fbAdInterests } = this.props
    if (_.find(fbAdInterests, ['id', interest.id])) {
      this.props.onFbAdInterestsChange(_.reject(fbAdInterests, ['id', interest.id]))
    } else {
      this.props.onFbAdInterestsChange([
        ...fbAdInterests,
        { id: interest.id, label: interest.label, isSelected: true }
      ])
    }
  }

  searchChanged(searchString: string) {
    this.setState({ searchString }, this.updateInterestsFn)
  }

  matchedNodes(interestList: Array<Node>): Array<Node> {
    const { searchString } = this.state
    const matcherFunc = utils.searchStringMatcher(searchString)
    return _.filter(interestList, ({ label }) => matcherFunc(label))
  }

  updateInterests() {
    const { searchString } = this.state
    if (_.trim(searchString) === '') return

    this.getInterests(searchString, (searchedFbAdInterests) => {
      this.setState({ searchedFbAdInterests })
    })
  }

  getInterests(queryString: string, callback: Function) {
    if (this.state.isFbInterestLoading) {
      this.setState({ searchWasChangedWhileLoading: true })
      return
    }
    this.setState({ isFbInterestLoading: true, fbInterestLoadingError: null })
    getJson(this.props.fbAdInterestsPath, { q: queryString })
      .done(({ data }) => {
        callback(data)
      })
      .fail(() => {
        this.setState({ fbInterestLoadingError: this.t('fb_ad_interests_loading_error_msg') })
      })
      .always(() => {
        this.setState({ isFbInterestLoading: false })
        if (this.state.searchWasChangedWhileLoading) {
          this.setState({ searchWasChangedWhileLoading: false })
          this.updateInterestsFn()
        }
      })
  }
}
